import lodash from 'lodash'
import { useMemo } from 'react'

import { useField } from '../../../../1stlog-ui/src/components/Field'
import { TNotificationOption } from '../Form/ClientNotifications/types'
import { TOrderEntryProduct, TOrderEntryProductSettings } from '../types'
import useOrderEntry from '../useOrderEntry'
import useProducts from './useProducts'

const useProductSettings = (): TOrderEntryProductSettings => {
  const { constantOrderEntryProduct } = useOrderEntry()

  const { products: pickupProducts = [] } = useProducts('pickup')

  const { products: deliveryProducts = [] } = useProducts('delivery')

  const pickupProductId = useField('pickup.selectedProductId', {
    ignorePrefix: true,
  })
  const deliveryProductId = useField('delivery.selectedProductId', {
    ignorePrefix: true,
  })

  const pickupProduct = useMemo(
    () =>
      lodash.find(
        pickupProducts,
        (product) => String(product.id) === String(pickupProductId),
      ),
    [pickupProducts, pickupProductId],
  )

  const deliveryProduct = useMemo(
    () =>
      lodash.find(
        deliveryProducts,
        (product) => String(product.id) === String(deliveryProductId),
      ),
    [deliveryProducts, deliveryProductId],
  )

  // union of all possible settings
  // TODO: cache this in recoil state to prevent mass recalc in every useProductSettings usage
  const settings = useMemo(
    () =>
      mergeProducts([
        pickupProduct,
        deliveryProduct,
        constantOrderEntryProduct,
      ]),
    [pickupProduct, deliveryProduct],
  )

  return settings
}

export default useProductSettings

function mergeProducts(
  allProducts: (TOrderEntryProduct | undefined)[],
): TOrderEntryProductSettings {
  const products: TOrderEntryProduct[] = allProducts.filter(
    Boolean,
  ) as TOrderEntryProduct[]
  return {
    isvalueOfGoodsAllowed: getBool(products, 'isvalueOfGoodsAllowed'),
    overwriteInvoiceAddressAllowed: getBool(
      products,
      'overwriteInvoiceAddressAllowed',
    ),
    shipmentServices: uniqueMergeBy(products, 'shipmentServices', 'name'),
    coliServices: uniqueMergeBy(products, 'coliServices', 'name'),
    notificationOptions: uniqueMergeBy<TNotificationOption>(
      products,
      'notificationOptions',
      (o) => `${o.type}_${o.trigger}`,
    ),
    neutralSenderActiveByDefault: getBool(
      products,
      'neutralSenderActiveByDefault',
    ),
    neutralSenderEditable: getBool(products, 'neutralSenderEditable'),
    neutralSendingEnabled: getBool(products, 'neutralSendingEnabled'),
    neutralSenderAddress:
      pickFirstAvailable(products, 'neutralSenderAddress') ||
      pickFirstAvailable(products, 'defaultNeutralSenderAddress'),
    hazmatAllowedUnNumberList: uniqueMergeBy(
      products,
      'hazmatAllowedUnNumberList',
    ),
    hazmatAllowedUnNumberRanges: uniqueMergeBy(
      products,
      'hazmatAllowedUnNumberRanges',
    ),
    allowedPackagingList: uniqueMergeBy(products, 'allowedPackagingList'),
    isPackagingAllowed: getBool(products, 'isPackagingAllowed'),
    isBarcodeInputAllowed: getBool(products, 'isBarcodeInputAllowed'),

    // unique identifier for this product combo
    settingsId: lodash.map(products.filter(Boolean), (p) => p.id).join('_'),

    // single values
    maxGirth: pickFirstAvailable(products, 'maxGirth'),
    maxHeight: pickFirstAvailable(products, 'maxHeight'),
    maxLength: pickFirstAvailable(products, 'maxLength'),
    maxVolume: pickFirstAvailable(products, 'maxVolume'),
    maxWeight: pickFirstAvailable(products, 'maxWeight'),
    maxWidth: pickFirstAvailable(products, 'maxWidth'),

    isContentDescriptionAllowed: getBool(
      products,
      'isContentDescriptionAllowed',
    ),
    reusableBoxAllowed: getBool(products, 'reusableBoxAllowed'),
    isFreightTermsAllowed: getBool(products, 'isFreightTermsAllowed'),
    allowedFreightTermList: uniqueMergeBy(products, 'allowedFreightTermList'),
    defaultFreightTerm: lodash.get(
      pickFirstAvailable(products, 'defaultFreightTerm'),
      'key',
    ),
    defaultPackaging: lodash.get(
      pickFirstAvailable(products, 'defaultPackaging'),
      'key',
    ),
    allowAnyPackaging: getBool(products, 'allowAnyPackaging'),
    isContentRequired: getBool(products, 'isContentRequired'),
    contentDescriptionMandatory: getBool(
      products,
      'contentDescriptionMandatory',
    ),
    isValueOfGoodsRequired: getBool(products, 'isValueOfGoodsRequired'),
    showVolumeWeight: getBool(products, 'showVolumeWeight'),
    volumeWeightFactor: pickFirstAvailable(products, 'volumeWeightFactor'),

    // anonyme erfassung flags
    isMeasurementsHidden: getBool(products, 'isMeasurementsHidden'),
    isMeasurementsOptional: getBool(products, 'isMeasurementsOptional'),
    isWeightHidden: getBool(products, 'isWeightHidden'),
    isWeightOptional: getBool(products, 'isWeightOptional'),
    isColiReferenceTextHidden: getBool(products, 'isColiReferenceTextHidden'),
    isColiReferenceTextMandantory: getBool(
      products,
      'isColiReferenceTextMandantory',
    ),
    eoriIsNeverMandatory: getBool(products, 'eoriIsNeverMandatory'),
    enableProforma: getBool(products, 'enableProforma'),
    enableDutiableProducts: getBool(products, 'enableDutiableProducts'),
    measurementsMandantory: getBool(products, 'measurementsMandantory'),
    isFreightCollectCheckAllowed: getBool(
      products,
      'isFreightCollectCheckAllowed',
    ),
    maxValueOfGoods: pickFirstAvailable(products, 'maxValueOfGoods'),
    isIncotermsHidden: getBool(products, 'isIncotermsHidden'),
    maxColis: pickFirstAvailable(products, 'maxColis'),
    isPrenameSurnameContactVisible: getBool(
      products,
      'isPrenameSurnameContactVisible',
    ),
    isFreightCollectReferenceHidden: getBool(
      products,
      'isFreightCollectReferenceHidden',
    ),
    isInvoiceUploadRequired: getBool(products, 'isInvoiceUploadRequired'),
    isPickupPhoneAlwaysRequired: getBool(
      products,
      'isPickupPhoneAlwaysRequired',
    ),
    isDeliveryPhoneAlwaysRequired: getBool(
      products,
      'isDeliveryPhoneAlwaysRequired',
    ),
    importerAddressIsDisplayed: getBool(products, 'importerAddressIsDisplayed'),
    disableDocumentUpload: getBool(products, 'disableDocumentUpload'),
    senderContactNameRequired: getBool(products, 'senderContactNameRequired'),
    receiverContactNameRequired: getBool(
      products,
      'receiverContactNameRequired',
    ),
  }
}

// function merge(products, property) {
//   const array = lodash
//     .concat(
//       lodash.flattenDepth(
//         lodash.map(products, (p) => lodash.get(p, property)),
//         1,
//       ),
//     )
//     .filter(Boolean)
//   return array
// }

function uniqueMergeBy<T>(
  products: TOrderEntryProduct[],
  property: string,
  by: lodash.ValueIteratee<any> = (o) => o,
): T[] {
  const array = lodash
    .concat(
      lodash.flattenDepth(
        lodash.map(products, (p) => lodash.get(p, property)),
        1,
      ),
    )
    .filter(Boolean)
  return lodash.uniqBy<T>(array, by)
}

function pickFirstAvailable<T>(
  products: TOrderEntryProduct[],
  property: string,
): T {
  return lodash.first(
    lodash
      .map(products, (product) => lodash.get(product, property))
      .filter((i) => i && i !== ''),
  )
}

// function getHighest(products, property) {
//   return lodash.first(lodash.maxBy(products, p => lodash.get(p, property))
// }

function getBool(products: TOrderEntryProduct[], property: string): boolean {
  return lodash.some(lodash.map(products, (p) => lodash.get(p, property)))
}
